import { Input, InputNumber, Select, Typography } from "antd";
// import { DatePicker } from "antd"; // TODO: for "until_date" feature when BE supports it
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { waitFormSchema } from "components/CC/Nodes/WaitNode/Forms/formSchema";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect } from "react";
import { FormItem } from "react-hook-form-antd";
import { z } from "zod";

// TODO: "until_date" not supported by BE yet - only delay_seconds
// type WaitType = "fixed_delay" | "until_date";

export type WaitFormData = z.infer<typeof waitFormSchema>;

interface FormProps {
    onChange?: (data: WaitFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
}

export const WaitForm = ({ onChange, onRegisterValidator, formId }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    const defaultValues = { active: true, ...(selectedNode?.formData as WaitFormData | undefined) } as WaitFormData;
    return (
        <GeneralFormProvider<WaitFormData>
            key={selectedNode?.id}
            id={formId}
            schema={waitFormSchema}
            defaultValues={defaultValues}
        >
            <FormContent onChange={onChange} onRegisterValidator={onRegisterValidator} />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: WaitFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
}

const FormContent = ({ onChange, onRegisterValidator }: FormContentProps) => {
    const { control, trigger } = useGeneralForm<WaitFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");

    // TODO: BE only supports fixed delay (delay_seconds), "until_date" not supported yet
    // const waitType = watch("type");
    // const typeOptions = [
    //     { value: "fixed_delay", label: t("nodes.wait.forms.type_options.fixed_delay") },
    //     { value: "until_date", label: t("nodes.wait.forms.type_options.until_date") },
    // ];

    // TODO: BE only supports seconds, add other units when BE supports conversion
    const unitOptions = [
        { value: "seconds", label: t("nodes.wait.forms.unit_options.seconds") },
        // { value: "minutes", label: t("nodes.wait.forms.unit_options.minutes") },
        // { value: "hours", label: t("nodes.wait.forms.unit_options.hours") },
        // { value: "days", label: t("nodes.wait.forms.unit_options.days") },
    ];

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<WaitFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.wait.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">{t("nodes.wait.forms.active")}</Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem className="flex-1 !mb-0" control={control} name="title" label={t("nodes.wait.forms.title")}>
                    <Input placeholder={t("nodes.wait.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.wait.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.wait.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Delay row - BE validation: min:0, max:31536000 (1 year in seconds) */}
            {/* TODO: BE only supports fixed delay (delay_seconds), type select commented out */}
            {/* <FormItem className="flex-1 !mb-0" control={control} name="type" label={t("nodes.wait.forms.type")}>
                <Select options={typeOptions} />
            </FormItem> */}
            <div className="flex-1">
                <Typography.Text className="block mb-2">{t("nodes.wait.forms.delay")}</Typography.Text>
                <div className="flex gap-2">
                    <FormItem className="flex-1 !mb-0" control={control} name="delayValue">
                        <InputNumber className="w-full" min={0} max={31536000} />
                    </FormItem>
                    <FormItem className="w-32 !mb-0" control={control} name="delayUnit">
                        <Select options={unitOptions} />
                    </FormItem>
                </div>
            </div>
            {/* TODO: "until_date" not supported by BE yet
            <FormItem className="flex-1 !mb-0" control={control} name="untilDate" label={t("nodes.wait.forms.delay")}>
                <DatePicker className="w-full" format="D.M.YYYY" />
            </FormItem>
            */}
        </div>
    );
};
